import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getBackendUrl } from "@/lib/backend";
import { useScope } from "@/context/ScopeContext";
import "./UsmRiskSchoolsModal.css";

const API = `${getBackendUrl()}/api`;
const PAGE_SIZE = 10;

const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? "—");
const pct = (v, digits = 2) => (typeof v === "number" ? `${v.toFixed(digits)}%` : "—");

export const AADHAAR_KPI_MODAL_CONFIG = {
  exception_rate: {
    id: "exception_rate",
    label: "Exception rate",
    fallbackTitle: "Exception Rate — Schools With Open Aadhaar Records",
  },
  open_aadhaar_issues: {
    id: "open_aadhaar_issues",
    label: "Exception rate",
    fallbackTitle: "Open Aadhaar Issues — Schools With Exception Backlog",
  },
  failed_records: {
    id: "failed_records",
    label: "Failed records",
    fallbackTitle: "Failed Records — Schools Requiring Immediate Correction",
  },
  name_mismatch: {
    id: "name_mismatch",
    label: "Name mismatch",
    fallbackTitle: "Name Mismatch — Schools With Identity Verification Gaps",
  },
  mbu_pending: {
    id: "mbu_pending",
    label: "MBU pending",
    fallbackTitle: "MBU Pending — Schools With Biometric Update Backlog",
  },
  high_risk_schools: {
    id: "high_risk_schools",
    label: "Exception rate",
    fallbackTitle: "High-Risk Schools — Exception Rate Above 10%",
  },
};

const EXCEPTION_TYPE_LABELS = {
  all: "open issue records",
  failed: "failed records",
  pending: "pending records",
  not_provided: "not-provided records",
};

const showExceptionTypeFilter = (metric) => metric === "open_aadhaar_issues";

const formatCoverageSummary = (metric, districtPct, districtAggregate, data) => {
  const rate = data?.district_pct ?? districtPct;
  const aggregate =
    typeof data?.district_aggregate === "number"
      ? data.district_aggregate
      : typeof districtAggregate === "number"
        ? districtAggregate
        : null;
  const issueLabel =
    EXCEPTION_TYPE_LABELS[data?.exception_type || "all"] || EXCEPTION_TYPE_LABELS.all;

  if ((metric === "exception_rate" || metric === "open_aadhaar_issues") && aggregate != null && rate != null) {
    return (
      <>
        <b>{fmt(aggregate)}</b> {issueLabel} · district rate <b>{pct(rate)}</b>
      </>
    );
  }
  if (metric === "failed_records" && aggregate != null && rate != null) {
    return (
      <>
        <b>{fmt(aggregate)}</b> failed records · district rate <b>{pct(rate)}</b>
      </>
    );
  }
  if (metric === "high_risk_schools" && aggregate != null) {
    return (
      <>
        <b>{fmt(aggregate)}</b> schools above 10% exception rate
        {rate != null ? (
          <>
            {" "}
            · district exception rate <b>{pct(rate)}</b>
          </>
        ) : null}
      </>
    );
  }
  if (metric === "name_mismatch" && aggregate != null && rate != null) {
    return (
      <>
        <b>{fmt(aggregate)}</b> mismatch records · district rate <b>{pct(rate)}</b>
      </>
    );
  }
  if (metric === "mbu_pending" && aggregate != null && rate != null) {
    return (
      <>
        <b>{fmt(aggregate)}</b> pending MBU updates · district rate <b>{pct(rate)}</b>
      </>
    );
  }
  return rate != null ? <>District rate <b>{pct(rate)}</b></> : "—";
};

const renderMetricCell = (metric, school) => {
  if (metric === "failed_records") {
    return fmt(school.aadhaar_failed ?? school.metric_pct);
  }
  if (metric === "exception_rate" || metric === "open_aadhaar_issues" || metric === "high_risk_schools") {
    return pct(school.exception_rate ?? school.metric_pct);
  }
  if (metric === "name_mismatch") {
    return pct(school.name_mismatch_pct ?? school.metric_pct);
  }
  return pct(school.mbu_pending_pct ?? school.metric_pct);
};

const showCountColumn = (metric) =>
  metric === "exception_rate" ||
  metric === "open_aadhaar_issues" ||
  metric === "high_risk_schools" ||
  metric === "failed_records" ||
  metric === "mbu_pending";

const countColumnLabel = (metric, exceptionType = "all") => {
  if (metric === "failed_records") return "Failed";
  if (metric === "mbu_pending") return "MBU pending";
  if (exceptionType === "failed") return "Failed";
  if (exceptionType === "pending") return "Pending";
  if (exceptionType === "not_provided") return "Not provided";
  return "Exceptions";
};

const renderCountCell = (metric, school, exceptionType = "all") => {
  if (metric === "failed_records") {
    return fmt(school.aadhaar_failed);
  }
  if (metric === "mbu_pending") {
    return fmt(school.mbu_pending_total);
  }
  if (exceptionType === "failed") return fmt(school.aadhaar_failed);
  if (exceptionType === "pending") return fmt(school.aadhaar_pending);
  if (exceptionType === "not_provided") return fmt(school.aadhaar_not_provided);
  return fmt(school.exception_total);
};

const AadhaarKpiSchoolsModal = ({ open, metric, onClose, scopeLabel, districtPct, districtAggregate }) => {
  const navigate = useNavigate();
  const { scope } = useScope();
  const config = metric ? AADHAAR_KPI_MODAL_CONFIG[metric] : null;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [blockFilter, setBlockFilter] = useState("");
  const [schoolFilter, setSchoolFilter] = useState("");
  const [exceptionTypeFilter, setExceptionTypeFilter] = useState("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (open && metric) {
      setExceptionTypeFilter("all");
      setQuery("");
      setBlockFilter("");
      setSchoolFilter("");
      setPage(1);
    }
  }, [open, metric]);

  useEffect(() => {
    if (!open || !metric) return undefined;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);

    const params = { metric, limit: 5000 };
    if (scope.districtCode) params.district_code = scope.districtCode;
    if (scope.blockCode) params.block_code = scope.blockCode;
    if (scope.udiseCode) params.udise_code = scope.udiseCode;
    if (scope.districtName) params.district_name = scope.districtName;
    if (scope.blockName) params.block_name = scope.blockName;
    if (scope.schoolName) params.school_name = scope.schoolName;
    if (!scope.districtName && !scope.districtCode) params.district_name = "PUNE";
    if (showExceptionTypeFilter(metric)) {
      params.exception_type = exceptionTypeFilter || "all";
    }

    axios
      .get(`${API}/aadhaar/kpi-schools`, { params })
      .then((res) => {
        if (!cancelled) setData(res.data || null);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.detail || "Failed to load school list");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    open,
    metric,
    scope.version,
    scope.districtCode,
    scope.blockCode,
    scope.udiseCode,
    scope.districtName,
    scope.blockName,
    scope.schoolName,
    exceptionTypeFilter,
  ]);

  const blockOptions = useMemo(() => {
    const schools = data?.schools || [];
    return [...new Set(schools.map((s) => s.block_name).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  }, [data?.schools]);

  const schoolOptions = useMemo(() => {
    const schools = data?.schools || [];
    const scoped = blockFilter
      ? schools.filter((s) => String(s.block_name || "") === blockFilter)
      : schools;
    return [...new Set(scoped.map((s) => s.school_name).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  }, [data?.schools, blockFilter]);

  const filtered = useMemo(() => {
    let schools = data?.schools || [];
    if (blockFilter) {
      schools = schools.filter((s) => String(s.block_name || "") === blockFilter);
    }
    if (schoolFilter) {
      schools = schools.filter((s) => String(s.school_name || "") === schoolFilter);
    }
    const q = query.trim().toLowerCase();
    if (!q) return schools;
    return schools.filter(
      (s) =>
        String(s.school_name || "").toLowerCase().includes(q) ||
        String(s.block_name || "").toLowerCase().includes(q) ||
        String(s.action_needed || "").toLowerCase().includes(q) ||
        String(s.udise_code || "").includes(q),
    );
  }, [data?.schools, blockFilter, schoolFilter, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const metricLabel = data?.metric_label || config?.label || "Value";
  const title = data?.title || config?.fallbackTitle || "Aadhaar KPI Schools";

  if (!open || !metric || !config) return null;

  return createPortal(
    <div className="usm-risk-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="usm-risk-modal usm-risk-modal-wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="aadhaar-kpi-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="usm-risk-modal-head">
          <div>
            <p className="usm-risk-modal-eyebrow">Aadhaar drill-down · {scopeLabel || "District scope"}</p>
            <h3 id="aadhaar-kpi-modal-title">{title}</h3>
            <p className="usm-risk-modal-sub">
              {formatCoverageSummary(metric, districtPct, districtAggregate, data)}
              {data?.description ? <> · {data.description}</> : null}
            </p>
          </div>
          <button type="button" className="usm-risk-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="usm-risk-modal-toolbar usm-risk-modal-toolbar-filters">
          <label className="usm-risk-modal-filter">
            <span>Block</span>
            <select
              value={blockFilter}
              onChange={(e) => {
                setBlockFilter(e.target.value);
                setSchoolFilter("");
                setPage(1);
              }}
              disabled={loading || !blockOptions.length}
            >
              <option value="">All blocks</option>
              {blockOptions.map((block) => (
                <option key={block} value={block}>
                  {block}
                </option>
              ))}
            </select>
          </label>
          <label className="usm-risk-modal-filter">
            <span>School</span>
            <select
              value={schoolFilter}
              onChange={(e) => {
                setSchoolFilter(e.target.value);
                setPage(1);
              }}
              disabled={loading || !schoolOptions.length}
            >
              <option value="">All schools</option>
              {schoolOptions.map((school) => (
                <option key={school} value={school}>
                  {school}
                </option>
              ))}
            </select>
          </label>
          {showExceptionTypeFilter(metric) ? (
            <label className="usm-risk-modal-filter">
              <span>Issue type</span>
              <select
                value={exceptionTypeFilter}
                onChange={(e) => {
                  setExceptionTypeFilter(e.target.value);
                  setPage(1);
                }}
                disabled={loading}
              >
                <option value="all">All issues</option>
                <option value="failed">Failed</option>
                <option value="pending">Pending</option>
                <option value="not_provided">Not provided</option>
              </select>
            </label>
          ) : null}
          {data ? (
            <span className="usm-risk-modal-count">
              Showing {fmt(filtered.length)} of {fmt(data.total_schools)} schools
            </span>
          ) : null}
        </div>

        <div className="usm-risk-modal-body">
          {loading ? (
            <div className="usm-risk-modal-loading">Loading schools…</div>
          ) : error ? (
            <div className="usm-risk-modal-error">{error}</div>
          ) : !data?.schools?.length ? (
            <div className="usm-risk-modal-empty">No schools match this gap in the current scope.</div>
          ) : (
            <>
              <div className="usm-risk-modal-body-search">
                <input
                  type="search"
                  className="usm-risk-modal-search"
                  placeholder="Search school, block, action, or UDISE…"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              {!pageRows.length ? (
                <div className="usm-risk-modal-empty">No schools match the selected block, school, or search.</div>
              ) : (
                <table className="usm-risk-modal-table usm-risk-modal-table-wide">
                  <thead>
                    <tr>
                      <th>Block</th>
                      <th>School</th>
                      <th>{metricLabel}</th>
                      {showCountColumn(metric) ? (
                        <th>{countColumnLabel(metric, data?.exception_type || exceptionTypeFilter)}</th>
                      ) : null}
                      <th>Students</th>
                      <th>Priority</th>
                      <th>UDISE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((school) => (
                      <tr
                        key={school.udise_code || `${school.school_name}-${school.block_name}`}
                        className="usm-risk-modal-row"
                        onClick={() => school.udise_code && navigate(`/school/${school.udise_code}`)}
                        title={school.udise_code ? `${school.action_needed || ""} — Open school detail` : school.action_needed}
                      >
                        <td>{school.block_name || "—"}</td>
                        <td>{school.school_name || "—"}</td>
                        <td>{renderMetricCell(metric, school)}</td>
                        {showCountColumn(metric) ? (
                          <td>{renderCountCell(metric, school, data?.exception_type || exceptionTypeFilter)}</td>
                        ) : null}
                        <td>{fmt(school.total_enrolment)}</td>
                        <td>
                          <span className={`usm-priority-pill ${String(school.priority || "").toLowerCase()}`}>
                            {school.priority || "—"}
                          </span>
                        </td>
                        <td>{school.udise_code || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>

        {!loading && !error && filtered.length > PAGE_SIZE ? (
          <div className="usm-risk-modal-footer">
            <button
              type="button"
              className="usm-risk-modal-page-btn"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className="usm-risk-modal-page-btn"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
};

export default AadhaarKpiSchoolsModal;
